import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * DiscountPicker — multi-select checkbox group of discount slugs.
 * Reads catalogue from /api/discount-catalogue so labels/percentages stay in
 * sync with backend resolution.
 *
 * Props:
 *  - value (string[]): selected discount keys
 *  - onChange (next:string[]) => void
 *  - testId (string)
 *  - showTotal (boolean): display the combined % under the list
 */
export default function DiscountPicker({ value = [], onChange, testId = 'discount-picker', showTotal = true }) {
  const { api } = useAuth();
  const [catalogue, setCatalogue] = useState([]);

  useEffect(() => {
    let mounted = true;
    api.get('/discount-catalogue')
      .then(r => { if (mounted) setCatalogue(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [api]);

  const toggle = (key) => {
    const has = value.includes(key);
    onChange(has ? value.filter(v => v !== key) : [...value, key]);
  };

  const total = catalogue.reduce((acc, d) => acc + (value.includes(d.key) ? d.percent : 0), 0);

  return (
    <div data-testid={testId} className="space-y-1.5">
      {catalogue.map(d => {
        const active = value.includes(d.key);
        return (
          <label
            key={d.key}
            data-testid={`${testId}-${d.key}`}
            className={`flex items-center gap-2 px-2.5 py-1.5 border rounded-md cursor-pointer text-sm transition-colors ${
              active
                ? 'bg-[#FFF7ED] border-[#D35400] text-[#A04000]'
                : 'bg-white border-[#E2E2DF] text-slate-700 hover:border-slate-300'
            }`}
          >
            <input
              type="checkbox"
              checked={active}
              onChange={() => toggle(d.key)}
              className="accent-[#D35400] w-3.5 h-3.5"
            />
            <span className="flex-1">{d.label}</span>
            <span className="font-mono-num text-xs font-semibold">{d.percent}%</span>
          </label>
        );
      })}
      {showTotal && (
        <div className={`mt-1.5 text-xs font-semibold ${total > 0 ? 'text-[#D35400]' : 'text-slate-400'} text-right`} data-testid={`${testId}-total`}>
          Total discount: <span className="font-mono-num">{total}%</span>
        </div>
      )}
    </div>
  );
}
